import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { Check, Search, ChevronDown, ArrowLeft, ArrowRight } from 'lucide-react';
import { interiorMenuData, InteriorOption } from '../../types/interiorConfig';
type SheetState = 'collapsed' | 'peek' | 'expanded';
const LEVEL_COLORS: Record<string, {
  bg: string;
  text: string;
  border: string;
}> = {
  all: {
    bg: 'bg-white',
    text: 'text-black',
    border: 'border-white/20'
  },
  standard: {
    bg: 'bg-teal-500',
    text: 'text-white',
    border: 'border-teal-400'
  },
  'level-1': {
    bg: 'bg-blue-500',
    text: 'text-white',
    border: 'border-blue-400'
  },
  'level-2': {
    bg: 'bg-amber-500',
    text: 'text-black',
    border: 'border-amber-400'
  },
  'level-3': {
    bg: 'bg-emerald-500',
    text: 'text-white',
    border: 'border-emerald-400'
  },
  'level-4': {
    bg: 'bg-purple-500',
    text: 'text-white',
    border: 'border-purple-400'
  },
  'level-5': {
    bg: 'bg-rose-500',
    text: 'text-white',
    border: 'border-rose-400'
  },
  'level-6': {
    bg: 'bg-red-500',
    text: 'text-white',
    border: 'border-red-400'
  },
  'level-7': {
    bg: 'bg-orange-500',
    text: 'text-white',
    border: 'border-orange-400'
  },
  'level-8': {
    bg: 'bg-cyan-500',
    text: 'text-white',
    border: 'border-cyan-400'
  },
  'level-9': {
    bg: 'bg-indigo-500',
    text: 'text-white',
    border: 'border-indigo-400'
  },
  'level-10': {
    bg: 'bg-pink-500',
    text: 'text-white',
    border: 'border-pink-400'
  },
  'level-11': {
    bg: 'bg-lime-500',
    text: 'text-black',
    border: 'border-lime-400'
  },
  'level-12': {
    bg: 'bg-fuchsia-500',
    text: 'text-white',
    border: 'border-fuchsia-400'
  }
};
const LEVEL_LABELS: Record<string, string> = {
  all: 'All Levels',
  standard: 'Standard',
  'level-1': 'Level 1',
  'level-2': 'Level 2',
  'level-3': 'Level 3',
  'level-4': 'Level 4',
  'level-5': 'Level 5',
  'level-6': 'Level 6',
  'level-7': 'Level 7',
  'level-8': 'Level 8',
  'level-9': 'Level 9',
  'level-10': 'Level 10',
  'level-11': 'Level 11',
  'level-12': 'Level 12'
};
interface MobileOptionsSheetProps {
  activeItem?: string;
  sheetState: SheetState;
  onSheetStateChange: (state: SheetState) => void;
  onActiveItemChange?: (itemName: string) => void;
  isStepMode?: boolean;
  currentStepIndex?: number;
  totalSteps?: number;
  currentStepCategory?: string;
  currentStepItemName?: string;
  onNextStep?: () => void;
  onPrevStep?: () => void;
}
export function MobileOptionsSheet({
  activeItem = 'Countertop',
  sheetState,
  onSheetStateChange,
  onActiveItemChange,
  isStepMode,
  currentStepIndex = 0,
  totalSteps = 0,
  currentStepCategory,
  currentStepItemName,
  onNextStep,
  onPrevStep
}: MobileOptionsSheetProps) {
  const kitchenCategory = interiorMenuData.find(c => c.id === 'main-options')?.children?.find(c => c.id === 'kitchen');
  const itemData = kitchenCategory?.children?.find(i => i.name === activeItem);
  const allOptions = itemData?.options || [];
  // Get all items in this category that have options
  const availableItems = (kitchenCategory?.children || []).filter(child => child.options && child.options.length > 0);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(allOptions.find(o => o.selected)?.id || (allOptions.length > 0 ? allOptions[0].id : null));
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [focusSearchOnExpand, setFocusSearchOnExpand] = useState(false);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const itemPickerTriggerRef = useRef<HTMLButtonElement>(null);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  // Reset selection when activeItem changes
  useEffect(() => {
    const opts = itemData?.options || [];
    setSelectedOptionId(opts.find(o => o.selected)?.id || (opts.length > 0 ? opts[0].id : null));
    setSearchQuery('');
    setSelectedLevel('all');
  }, [activeItem]);
  // Derive which levels actually exist in the current options
  const availableLevels = ['all', ...Array.from(new Set(allOptions.map(o => o.level).filter(Boolean) as string[])).sort((a, b) => {
    if (a === 'standard') return -1;
    if (b === 'standard') return 1;
    const numA = parseInt(a.replace('level-', ''));
    const numB = parseInt(b.replace('level-', ''));
    return numA - numB;
  })];
  const filteredOptions = allOptions.filter(opt => {
    const matchesSearch = opt.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLevel = selectedLevel === 'all' || opt.level === selectedLevel;
    return matchesSearch && matchesLevel;
  });
  useEffect(() => {
    if (sheetState === 'expanded' && focusSearchOnExpand) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      setFocusSearchOnExpand(false);
    }
  }, [sheetState, focusSearchOnExpand]);
  const heights = {
    collapsed: 44,
    peek: isStepMode ? 210 : 170,
    expanded: 600
  };
  const handleDragEnd = (_event: any, info: PanInfo) => {
    const velocity = info.velocity.y;
    const offset = info.offset.y;
    if (sheetState === 'expanded') {
      if (velocity > 400 || offset > 80) onSheetStateChange('peek');
    } else if (sheetState === 'peek') {
      if (velocity < -400 || offset < -80) onSheetStateChange('expanded');else if (velocity > 400 || offset > 60) onSheetStateChange('collapsed');
    } else if (sheetState === 'collapsed') {
      if (velocity < -400 || offset < -40) onSheetStateChange('peek');
    }
  };
  const handleToggle = () => {
    if (sheetState === 'collapsed') onSheetStateChange('peek');else if (sheetState === 'peek') onSheetStateChange('expanded');else onSheetStateChange('peek');
  };
  // Update trigger rect when popover opens
  useEffect(() => {
    if (showItemPicker && itemPickerTriggerRef.current) {
      setTriggerRect(itemPickerTriggerRef.current.getBoundingClientRect());
    }
  }, [showItemPicker]);
  return <motion.div className="absolute bottom-0 left-0 right-0 z-40 bg-slate-900/85 backdrop-blur-2xl border-t border-white/10 rounded-t-2xl flex flex-col" initial={false} animate={{
    height: heights[sheetState]
  }} transition={{
    type: 'spring',
    damping: 28,
    stiffness: 220
  }} data-id="element-1986">
      {/* Progress Bar (Step Mode) */}
      {isStepMode && sheetState !== 'collapsed' && <div className="absolute top-0 left-0 right-0 h-1 bg-white/10 rounded-t-2xl overflow-hidden" data-id="element-1987">
          <motion.div className="h-full bg-blue-500" initial={false} animate={{
        width: `${(currentStepIndex + 1) / totalSteps * 100}%`
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-1988" />
        </div>}

      {/* Drag zone */}
      <motion.div drag="y" dragConstraints={{
      top: 0,
      bottom: 0
    }} dragElastic={0.15} onDragEnd={handleDragEnd} className="w-full shrink-0 cursor-grab active:cursor-grabbing relative z-10" style={{
      touchAction: 'none'
    }} onClick={handleToggle} data-id="element-1989">
        <div className="w-full h-6 flex items-center justify-center" data-id="element-1990">
          <div className="w-10 h-1 bg-white/25 rounded-full" data-id="element-1991" />
        </div>
      </motion.div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col px-3 relative z-10" data-id="element-1992">
        {/* Step-by-Step Header (replaces standard header in step mode) */}
        {isStepMode && sheetState !== 'collapsed' && <div className="flex items-center justify-between mb-3 shrink-0" data-id="element-1993">
            {/* Prev Arrow */}
            <button onClick={e => {
          e.stopPropagation();
          onPrevStep?.();
        }} disabled={currentStepIndex === 0} className={`p-2 rounded-full border transition-colors ${currentStepIndex === 0 ? 'border-white/5 text-white/20' : 'border-white/10 active:bg-white/10 text-white'}`} data-id="element-1994">
              <ArrowLeft className="w-4 h-4" data-id="element-1995" />
            </button>

            {/* Center: Category & Item (tappable to open item picker) */}
            <button ref={itemPickerTriggerRef} onClick={e => {
          e.stopPropagation();
          setShowItemPicker(!showItemPicker);
        }} className="flex-1 text-center min-w-0 px-2 active:opacity-70 transition-opacity" data-id="element-1996">
              <div className="text-[9px] uppercase tracking-wider text-white/40 mb-0.5" data-id="element-1997">
                {currentStepCategory} · {currentStepIndex + 1}/{totalSteps}
              </div>
              <div className="flex items-center justify-center gap-1" data-id="element-1998">
                <span className="text-sm font-bold text-white leading-snug truncate" data-id="element-1999">
                  {currentStepItemName}
                </span>
                <ChevronDown className={`w-3 h-3 text-white/40 shrink-0 transition-transform ${showItemPicker ? 'rotate-180' : ''}`} data-id="element-2000" />
              </div>
            </button>

            {/* Next Arrow */}
            <button onClick={e => {
          e.stopPropagation();
          onNextStep?.();
        }} disabled={currentStepIndex === totalSteps - 1} className={`p-2 rounded-full border transition-colors ${currentStepIndex === totalSteps - 1 ? 'border-white/5 text-white/20' : 'border-white/10 active:bg-white/10 text-white'}`} data-id="element-2001">
              <ArrowRight className="w-4 h-4" data-id="element-2002" />
            </button>
          </div>}

        {/* Standard Header (hidden in step mode unless expanded) */}
        {(!isStepMode || sheetState === 'expanded') && sheetState !== 'collapsed' && <div className="flex items-center justify-between mb-2 shrink-0 relative" data-id="element-2003">
              <button ref={!isStepMode ? itemPickerTriggerRef : undefined} onClick={e => {
          e.stopPropagation();
          setShowItemPicker(!showItemPicker);
        }} className="flex items-center gap-1.5 active:opacity-70 transition-opacity" data-id="element-2004">
                <h3 className="text-white font-semibold text-base" data-id="element-2005">
                  {activeItem}
                </h3>
                <ChevronDown className={`w-3.5 h-3.5 text-white/50 transition-transform ${showItemPicker ? 'rotate-180' : ''}`} data-id="element-2006" />
              </button>
              <div className="flex items-center gap-3" data-id="element-2007">
                {sheetState === 'peek' && <button onClick={e => {
            e.stopPropagation();
            setFocusSearchOnExpand(true);
            onSheetStateChange('expanded');
          }} className="p-1.5 bg-white/10 rounded-full active:bg-white/20 transition-colors" data-id="element-2008">
                    <Search className="w-3.5 h-3.5 text-white/80" data-id="element-2009" />
                  </button>}
                <span className="text-white/40 text-xs" data-id="element-2010">
                  {sheetState === 'expanded' && filteredOptions.length !== allOptions.length ? `${filteredOptions.length} of ${allOptions.length} options` : `${allOptions.length} options`}
                </span>
              </div>
            </div>}

        {/* Item Picker Popover - rendered via Portal */}
        {createPortal(<AnimatePresence data-id="element-2011">
            {showItemPicker && triggerRect && <>
                <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="fixed inset-0 z-[200]" onClick={() => setShowItemPicker(false)} data-id="element-2012" />
                <motion.div initial={{
            opacity: 0,
            y: 8,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: 8,
            scale: 0.95
          }} transition={{
            type: 'spring',
            damping: 25,
            stiffness: 300
          }} className="fixed z-[201] bg-slate-800/95 backdrop-blur-2xl rounded-xl border border-white/10 shadow-2xl overflow-hidden" style={{
            bottom: `${window.innerHeight - triggerRect.top + 8}px`,
            left: `${triggerRect.left - 12}px`,
            right: `${window.innerWidth - triggerRect.right - 12}px`,
            maxHeight: '300px'
          }} data-id="element-2013">
                  <div className="p-1.5 max-h-[300px] overflow-y-auto custom-scrollbar" data-id="element-2014">
                    {availableItems.map(item => {
                const isActive = item.name === activeItem;
                const optCount = item.options?.length || 0;
                return <button key={item.id} onClick={() => {
                  onActiveItemChange?.(item.name);
                  setShowItemPicker(false);
                }} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${isActive ? 'bg-white/10' : 'active:bg-white/5'}`} data-id="element-2015">
                          <span className={`text-sm font-medium ${isActive ? 'text-white' : 'text-white/70'}`} data-id="element-2016">
                            {item.name}
                          </span>
                          <div className="flex items-center gap-2" data-id="element-2017">
                            <span className="text-[11px] text-white/30" data-id="element-2018">
                              {optCount}
                            </span>
                            {isActive && <Check className="w-3.5 h-3.5 text-blue-400" data-id="element-2019" />}
                          </div>
                        </button>;
              })}
                  </div>
                </motion.div>
              </>}
          </AnimatePresence>, document.body)}

        {/* Expanded: Search & Filters */}
        {sheetState === 'expanded' && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex flex-col gap-2.5 mb-3 shrink-0" data-id="element-2020">
            {/* Search */}
            <div className="relative" data-id="element-2021">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" data-id="element-2022" />
              <input ref={searchInputRef} type="text" placeholder="Search options..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white/10 border border-white/5 rounded-full py-2 pl-9 pr-4 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-white/30 transition-shadow" data-id="element-2023" />
            </div>

            {/* Level filter chips */}
            <div className="flex gap-1.5 overflow-x-auto hide-scrollbar pb-1" data-id="element-2024">
              {availableLevels.map(level => {
            const isActive = selectedLevel === level;
            const colors = LEVEL_COLORS[level] || LEVEL_COLORS.all;
            const label = LEVEL_LABELS[level] || level;
            return <motion.button key={level} onClick={() => setSelectedLevel(level)} whileTap={{
              scale: 0.95
            }} className={`relative shrink-0 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all border ${isActive ? `${colors.bg} ${colors.text} ${colors.border}` : 'bg-white/5 text-white/50 border-white/5 active:bg-white/10'}`} data-id="element-2025">
                    {label}
                  </motion.button>;
          })}
            </div>
          </motion.div>}

        {/* Peek: Horizontal thumbnail scroll */}
        {sheetState === 'peek' && <div className="flex gap-2.5 overflow-x-auto hide-scrollbar pb-2" data-id="element-2026">
            {allOptions.map(option => <button key={option.id} onClick={() => setSelectedOptionId(option.id)} className={`relative shrink-0 w-20 flex flex-col gap-1.5 rounded-xl p-1 transition-colors ${selectedOptionId === option.id ? 'bg-white/10 ring-1 ring-white/30' : ''}`} data-id="element-2027">
                <div className="w-full h-16 rounded-lg overflow-hidden bg-slate-800 relative" data-id="element-2028">
                  {option.thumbnail && <img src={option.thumbnail} alt={option.name} className="w-full h-full object-cover" data-id="element-2029" />}
                  {selectedOptionId === option.id && <div className="absolute top-1 right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center" data-id="element-2030">
                      <Check className="w-2.5 h-2.5 text-white" data-id="element-2031" />
                    </div>}
                </div>
                <span className="text-[10px] leading-tight text-white/80 text-center truncate w-full" data-id="element-2032">
                  {option.name}
                </span>
              </button>)}
          </div>}

        {/* Expanded: Full grid */}
        {sheetState === 'expanded' && <div className="grid grid-cols-3 gap-2 overflow-y-auto custom-scrollbar pb-8" data-id="element-2033">
            {filteredOptions.length > 0 ? filteredOptions.map(option => {
          const levelColor = option.level ? LEVEL_COLORS[option.level] : null;
          return <button key={option.id} onClick={() => setSelectedOptionId(option.id)} className={`relative flex flex-col gap-1.5 rounded-xl p-1.5 transition-colors ${selectedOptionId === option.id ? 'bg-white/10 ring-1 ring-white/30' : 'bg-white/5'}`} data-id="element-2034">
                    <div className="w-full aspect-square rounded-lg overflow-hidden bg-slate-800 relative" data-id="element-2035">
                      {option.thumbnail && <img src={option.thumbnail} alt={option.name} className="w-full h-full object-cover" data-id="element-2036" />}
                      {selectedOptionId === option.id && <div className="absolute top-1 right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shadow-lg" data-id="element-2037">
                          <Check className="w-3 h-3 text-white" data-id="element-2038" />
                        </div>}
                      {/* Level badge */}
                      {levelColor && option.level && <div className={`absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[8px] font-bold ${levelColor.bg} ${levelColor.text}`} data-id="element-2039">
                          {option.level === 'standard' ? 'STD' : option.level.replace('level-', 'L')}
                        </div>}
                    </div>
                    <span className="text-[11px] font-medium text-white text-center truncate w-full" data-id="element-2040">
                      {option.name}
                    </span>
                  </button>;
        }) : <div className="col-span-3 py-12 flex flex-col items-center justify-center text-white/40" data-id="element-2041">
                <Search className="w-8 h-8 mb-3 opacity-20" data-id="element-2042" />
                <p className="text-sm" data-id="element-2043">No options found</p>
              </div>}
          </div>}
      </div>
    </motion.div>;
}